/** At most five actions stay inline. The rest go into the ellipsis. */
export const MAX_INLINE_TOOLBAR_ACTIONS = 5;

/** Popovers stay this far from the viewport edge. */
export const TOOLBAR_VIEWPORT_MARGIN = 20;

/** Menus scroll once they contain more than this many rows. */
export const TOOLBAR_MAX_MENU_ROWS = 15;

export const DEFAULT_TOOLBAR_COUNT_LIMIT = 10_000;

/**
 * Thousands separators follow the app language, not the browser locale.
 * English groups with a comma (10,000). German groups with a dot (10.000).
 */
export function formatToolbarCount(value: number, languageKey?: string | null): string {
    const locale = languageKey ? languageKey.replace(/_/g, '-') : undefined;
    return value.toLocaleString(locale);
}

export type ToolbarCounterMode = 'partial' | 'all' | 'over-limit' | 'all-entries';

export type ToolbarMenuSide = 'top' | 'bottom';

/**
 * How many actions fit inline.
 *
 * Actions are taken from the start of the list. Anything past the returned
 * count overflows into the ellipsis menu, which lists the first overflowed
 * action closest to the toolbar (the bottom row when the menu opens upwards).
 *
 * `availableWidth` is the space for the action slots and, when needed, the
 * ellipsis. `gap` is the space between those slots.
 */
export function resolveInlineActionCount(
    actionWidths: number[],
    availableWidth: number,
    ellipsisWidth: number,
    gap = 0,
): number {
    const total = actionWidths.length;
    if (total === 0) return 0;

    const widthOf = (count: number, withEllipsis: boolean) => {
        const pieces = count + (withEllipsis ? 1 : 0);
        if (pieces === 0) return 0;
        const actions = actionWidths.slice(0, count).reduce((sum, width) => sum + width, 0);
        const ellipsis = withEllipsis ? ellipsisWidth : 0;
        const gaps = gap * Math.max(0, pieces - 1);
        return actions + ellipsis + gaps;
    };

    const cap = Math.min(total, MAX_INLINE_TOOLBAR_ACTIONS);
    if (cap === total && widthOf(cap, false) <= availableWidth) {
        return cap;
    }

    let count = 0;
    for (let i = 1; i <= cap; i++) {
        if (widthOf(i, true) <= availableWidth) count = i;
        else break;
    }
    return count;
}

export function resolveToolbarCounterMode(input: {
    allSelected: boolean;
    overCountLimit: boolean;
}): ToolbarCounterMode {
    if (input.overCountLimit) {
        return input.allSelected ? 'all-entries' : 'over-limit';
    }
    return input.allSelected ? 'all' : 'partial';
}

/**
 * Every toolbar popover uses the same side. The menu opens upward (top,
 * aligned to the trigger's right edge) unless the content would cross the
 * viewport margin, in which case every popover opens downward.
 */
export function resolveToolbarMenuPlacement(input: {
    spaceAbove: number;
    spaceBelow: number;
    rowCount: number;
    rowHeight: number;
}): { side: ToolbarMenuSide; maxHeight: number } {
    const rowHeight = Math.max(1, input.rowHeight);
    const visibleRows = Math.min(Math.max(input.rowCount, 1), TOOLBAR_MAX_MENU_ROWS);
    const contentHeight = visibleRows * rowHeight;
    const side: ToolbarMenuSide = input.spaceAbove >= contentHeight + TOOLBAR_VIEWPORT_MARGIN
        ? 'top'
        : 'bottom';
    const space = side === 'top' ? input.spaceAbove : input.spaceBelow;
    const cap = rowHeight * TOOLBAR_MAX_MENU_ROWS;
    const room = Math.max(rowHeight, space - TOOLBAR_VIEWPORT_MARGIN);
    return { side, maxHeight: Math.min(cap, room) };
}
